import { Alert, Button, Empty, Spin } from 'antd';
import { Clock3, Hourglass, Info, Loader2, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getWorkflowPublishEvents,
  listWorkflowHistory,
  type WorkflowPublishEvent,
  type WorkflowRunHistoryItem,
  type WorkflowRunOverview,
  type WorkflowRunStatus,
} from '@/apis/logiblocksAPI';
import useToken from '@/common/helper/useToken';
import { ChangesPublishedIcon } from './ChangesPublishedIcon';
import { RunCompletedIcon, RunFailedIcon } from './runStatusIcons';
import { RunFailuresTooltip } from './RunFailuresTooltip';
import styles from './WorkflowRunHistoryPanel.module.css';

const PAGE_SIZE = 25;

// A single row of the history timeline: either a run, or a marker for when the
// workflow was republished. Publish markers are passive; only runs are clickable.
type RunHistoryRow =
  | { kind: 'run'; run: WorkflowRunHistoryItem }
  | { kind: 'published'; event: WorkflowPublishEvent };

// Runs are listed newest first and fetched a page at a time by position, so a
// run started since the previous page was fetched pushes the others down one
// place, and the next page repeats a run we already have. Combining the two by
// id drops those repeats.
function mergeRuns(
  incoming: WorkflowRunHistoryItem[],
  existing: WorkflowRunHistoryItem[]
): WorkflowRunHistoryItem[] {
  const runsById = new Map(existing.map((run) => [run.id, run]));
  for (const run of incoming) {
    // The newly fetched copy of a run we already hold carries its later status.
    runsById.set(run.id, run);
  }
  // Live and test runs number separately, so only the start time orders them together.
  return [...runsById.values()].sort(
    (a, b) => Date.parse(b.started_at) - Date.parse(a.started_at)
  );
}

// Every page the list currently holds. Pages are only ever added in order from
// the first, so the count of them is also the highest page number held.
function loadedPageNumbers(loadedPageCount: number): number[] {
  return Array.from(
    { length: Math.max(1, loadedPageCount) },
    (_, index) => index + 1
  );
}

// The instant a timeline row sits at: a run's start, or when a version published.
function rowTimestampMs(row: RunHistoryRow): number {
  return Date.parse(
    row.kind === 'run' ? row.run.started_at : row.event.published_at
  );
}

// Interleaves runs and publish markers into one timeline, newest first, by when
// each happened: a run's start, or when a version was published.
//
// A publish older than the oldest loaded run is held back until "Load more"
// brings in the runs around it, so an old publish never clumps at the bottom of
// a half-loaded list. Once every run is loaded (or there are none), every
// publish is shown.
function buildDisplayRows(
  runs: WorkflowRunHistoryItem[],
  publishEvents: WorkflowPublishEvent[],
  totalCount: number
): RunHistoryRow[] {
  const allRunsLoaded = runs.length >= totalCount;
  const oldestLoadedRunStartMs =
    runs.length > 0 ? Date.parse(runs[runs.length - 1].started_at) : null;
  const rows: RunHistoryRow[] = runs.map((run) => ({ kind: 'run', run }));
  for (const event of publishEvents) {
    const withinLoadedRange =
      oldestLoadedRunStartMs !== null &&
      Date.parse(event.published_at) >= oldestLoadedRunStartMs;
    if (allRunsLoaded || withinLoadedRange) {
      rows.push({ kind: 'published', event });
    }
  }
  return rows.sort((a, b) => rowTimestampMs(b) - rowTimestampMs(a));
}

interface WorkflowRunHistoryPanelProps {
  workflowId: string;
  /**
   * The run currently painted onto the graph, owned by LBCanvas. Null before
   * the first run list has loaded, and when the workflow has no runs.
   */
  selectedRunId: string | null;
  /** Counts the selected run's status changes, from `useWorkflowRunDetail`. Each change reloads the list. */
  runStatusChangeCount: number;
  /** Reports a row click, or null when no run should be selected. */
  onSelectRunId: (runId: string | null) => void;
  /** Repaints the graph for the selected run. Fired alongside the list reload. */
  onRefreshRunDetail: () => void;
}

function formatDuration(durationMs: number | null): string {
  if (durationMs == null) return 'In progress';
  if (durationMs < 1000) return `${durationMs}ms`;

  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

// Formats the average run duration for the Overview card, or a dash when no
// run has completed yet.
function formatMetricRuntime(durationMs: number | null): string {
  if (durationMs == null) {
    return '-';
  }
  if (durationMs < 1000) {
    return `${durationMs} ms`;
  }

  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) {
    return `${seconds} s`;
  }
  return `${minutes} m ${seconds} s`;
}

// How long a Delay waited, as its editor writes the time: "72 hr", "1 hr 30 min", "15 min". The
// run's other durations stay in seconds, but a wait is minutes to days.
function formatWaited(durationMs: number | null): string {
  if (durationMs === null) return 'its time limit';
  const totalMinutes = Math.round(durationMs / 60_000);
  if (totalMinutes < 1) return 'under a minute';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;
}

function formatRunLabel(run: WorkflowRunHistoryItem): string {
  if (run.status === 'FAILED')
    return `Failed in ${formatDuration(run.duration_ms)}`;
  if (run.status === 'COMPLETED') {
    return `Completed in ${formatDuration(run.duration_ms)}`;
  }
  if (run.status === 'CANCELED') {
    return `Canceled in ${formatDuration(run.duration_ms)}`;
  }
  // How long it waited is the story of this run, so the label says it in the wait's own words.
  if (run.status === 'WAIT_EXPIRED') {
    return `Wait expired after ${formatWaited(run.duration_ms)}`;
  }
  if (run.status === 'SUSPENDED') return 'Suspended';
  return 'In progress';
}

// The API sends a UTC ISO-8601 instant; show it in the viewer's own timezone.
// Month/day order follows the user's date_format preference; there's no year
// shown here, so YYYY/MM/DD is treated the same as MM/DD/YYYY (month-first).
function formatTimestamp(value: string | null, dateFormat?: string): string {
  if (!value) return 'Unknown';
  const d = new Date(value);
  const month = d.toLocaleString(undefined, { month: 'short' });
  const day = d.toLocaleString(undefined, { day: '2-digit' });
  const datePart =
    dateFormat === 'DD/MM/YYYY' ? `${day} ${month}` : `${month} ${day}`;
  const timePart = d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return `${datePart}, ${timePart}`;
}

function StatusIcon({ status }: { status: WorkflowRunStatus }) {
  switch (status) {
    // A canceled run shares the failed run's icon: both mean the run stopped
    // before finishing, and the label beside it says which.
    case 'CANCELED':
    case 'FAILED':
      return <RunFailedIcon className={styles.failedIcon} size={14} />;
    case 'WAIT_EXPIRED':
      return (
        <Hourglass
          aria-label='Wait expired'
          className={styles.neutralIcon}
          size={14}
        />
      );
    case 'COMPLETED':
      return <RunCompletedIcon className={styles.successIcon} size={14} />;
    case 'SUSPENDED':
      return (
        <Clock3
          aria-label='Suspended'
          className={styles.neutralIcon}
          size={14}
        />
      );
    case 'RUNNING':
      return (
        <Loader2
          aria-label='Running'
          className={styles.spinnerIcon}
          size={14}
        />
      );
    default:
      console.warn(`Unexpected workflow run status: ${status}`);
      return (
        <Loader2
          aria-label='Running'
          className={styles.spinnerIcon}
          size={14}
        />
      );
  }
}

export function WorkflowRunHistoryPanel({
  workflowId,
  selectedRunId,
  runStatusChangeCount,
  onSelectRunId,
  onRefreshRunDetail,
}: WorkflowRunHistoryPanelProps) {
  const { getToken, decodedUserToken } = useToken();
  const dateFormat = decodedUserToken()?.date_format;
  // Null until the first page arrives, which is what the spinner waits on.
  const [runs, setRuns] = useState<WorkflowRunHistoryItem[] | null>(null);
  const [overview, setOverview] = useState<WorkflowRunOverview | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loadedPageCount, setLoadedPageCount] = useState(0);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  // Empty rather than null: publishes only ever add markers to the timeline, so
  // a workflow with none, or one whose publishes fail to load, shows the runs
  // alone rather than a blank or error state.
  const [publishEvents, setPublishEvents] = useState<WorkflowPublishEvent[]>(
    []
  );
  const requestedWorkflowId = useRef(workflowId);

  const fetchPages = useCallback(
    async (pages: number[]) => {
      const token = getToken();
      if (!token) {
        setListError('Authentication is required to load run history.');
        return;
      }

      requestedWorkflowId.current = workflowId;
      setIsLoadingList(true);
      setListError(null);
      try {
        const responses = await Promise.all(
          pages.map((page) =>
            listWorkflowHistory(token, workflowId, {
              page,
              pageSize: PAGE_SIZE,
              status: 'ALL',
            })
          )
        );
        // A response for a workflow the user has already left would merge that
        // workflow's runs into this one's list.
        if (workflowId !== requestedWorkflowId.current) {
          return;
        }
        const [firstPage] = responses;
        // The totals cover the whole workflow rather than any one page, so every
        // response carries the same ones.
        setOverview(firstPage.overview);
        setTotalCount(firstPage.pagination.total_count);
        // The reported pages, not the requested ones: the API clamps a page past
        // the end of the list back to the last one.
        setLoadedPageCount(
          Math.max(...responses.map((response) => response.pagination.page))
        );
        setRuns((current) =>
          responses.reduce(
            (merged, response) => mergeRuns(response.results, merged),
            current ?? []
          )
        );
      } catch (error) {
        if (workflowId !== requestedWorkflowId.current) {
          return;
        }
        // One page failing leaves the whole list as it was, rather than
        // half-updating it.
        console.error('Failed to load workflow run history:', error);
        setListError('Failed to load run history.');
      } finally {
        if (workflowId === requestedWorkflowId.current) {
          setIsLoadingList(false);
        }
      }
    },
    [getToken, workflowId]
  );

  // Publishes rarely change, so they load once per workflow and are reused as
  // the run list pages. A failure here leaves the runs untouched.
  const fetchPublishEvents = useCallback(() => {
    const token = getToken();
    if (!token) {
      setPublishEvents([]);
      return;
    }
    requestedWorkflowId.current = workflowId;
    getWorkflowPublishEvents(token, workflowId)
      .then((events) => {
        if (workflowId === requestedWorkflowId.current) {
          setPublishEvents(events);
        }
      })
      .catch((error) => {
        if (workflowId === requestedWorkflowId.current) {
          console.error('Failed to load workflow publish events:', error);
          setPublishEvents([]);
        }
      });
  }, [getToken, workflowId]);

  useEffect(() => {
    const _ = fetchPages([1]);
    fetchPublishEvents();
  }, [fetchPages, fetchPublishEvents]);

  // Runs and publishes are kept as they load, so drop another workflow's before
  // showing this one's.
  useEffect(() => {
    setRuns(null);
    setLoadedPageCount(0);
    setPublishEvents([]);
  }, [workflowId]);

  // Reloads the list and repaints the graph, for a run that has moved on since
  // it was opened. Every loaded page reloads, not just the first: a suspended
  // run newer runs have pushed further down the list moves on too.
  const handleRefresh = useCallback(() => {
    const _ = fetchPages(loadedPageNumbers(loadedPageCount));
    fetchPublishEvents();
    onRefreshRunDetail();
  }, [fetchPages, fetchPublishEvents, loadedPageCount, onRefreshRunDetail]);

  // The `runStatusChangeCount` the list last loaded for. The mount fetch covers the first value.
  const listedRunStatusChangeCount = useRef(runStatusChangeCount);

  // Reloads every loaded page when the selected run changes status, so its row and the overview
  // metrics match the graph.
  useEffect(() => {
    if (runStatusChangeCount !== listedRunStatusChangeCount.current) {
      listedRunStatusChangeCount.current = runStatusChangeCount;
      const _ = fetchPages(loadedPageNumbers(loadedPageCount));
    }
  }, [runStatusChangeCount, fetchPages, loadedPageCount]);

  // Selection is owned by LBCanvas, so keep it pointed at a real run. Default to
  // the newest run when nothing valid is selected, so opening Runs visualizes the
  // latest run, and clear it when the workflow has no runs.
  useEffect(() => {
    if (runs === null) {
      return;
    }
    const stillSelected =
      selectedRunId !== null && runs.some((run) => run.id === selectedRunId);
    if (!stillSelected) {
      onSelectRunId(runs[0]?.id ?? null);
    }
  }, [runs, selectedRunId, onSelectRunId]);

  const metrics = useMemo(
    () => [
      { label: 'In progress', value: overview?.in_progress ?? 0 },
      {
        label: 'Avg runtime',
        value: formatMetricRuntime(overview?.avg_runtime_ms ?? null),
      },
      { label: 'Completed', value: overview?.completed ?? 0 },
      { label: 'Failed', value: overview?.failed ?? 0 },
      { label: 'Wait expired', value: overview?.wait_expired ?? 0 },
    ],
    [overview]
  );

  // Runs interleaved with publish markers, once the first run page has loaded.
  const displayRows = useMemo(
    () =>
      runs === null ? [] : buildDisplayRows(runs, publishEvents, totalCount),
    [runs, publishEvents, totalCount]
  );

  return (
    <aside className={styles.panel} aria-label='Workflow run history'>
      <div className={styles.content}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Overview</h2>
          <Button
            size='small'
            icon={<RefreshCw size={14} />}
            loading={isLoadingList}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </div>
        <div className={styles.metricGrid}>
          {metrics.map((metric) => (
            <div key={metric.label} className={styles.metric}>
              <div className={styles.metricLabel}>{metric.label}</div>
              <div className={styles.metricValue}>{metric.value}</div>
            </div>
          ))}
        </div>

        <div className={styles.divider} />

        <h2 className={styles.sectionTitle}>Run history</h2>

        {listError && (
          <Alert
            type='error'
            message={listError}
            action={
              <Button
                size='small'
                onClick={() => {
                  const _ = fetchPages([1]);
                }}
              >
                Retry
              </Button>
            }
          />
        )}
        {isLoadingList && runs === null && (
          <div className={styles.centerState}>
            <Spin />
          </div>
        )}
        {!isLoadingList &&
          !listError &&
          runs !== null &&
          displayRows.length === 0 && (
            <Empty
              description='No runs yet'
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        {displayRows.length > 0 && (
          <div className={styles.runList}>
            {displayRows.map((row) => {
              // A passive marker of when the workflow changed. It cannot be
              // selected, so it is a plain row rather than a run button.
              if (row.kind === 'published') {
                return (
                  <div
                    key={`published:${row.event.version_number}`}
                    className={styles.publishRow}
                  >
                    <ChangesPublishedIcon
                      className={styles.neutralIcon}
                      size={14}
                    />
                    <span className={styles.runMain}>Changes published</span>
                    <span className={styles.runTime}>
                      {formatTimestamp(row.event.published_at, dateFormat)}
                    </span>
                  </div>
                );
              }
              const { run } = row;
              const hasFailures =
                run.status === 'FAILED' || run.failure_reasons.length > 0;
              const rowButton = (
                <button
                  key={run.id}
                  type='button'
                  className={`${styles.runRow} ${
                    selectedRunId === run.id ? styles.runRowActive : ''
                  }`}
                  onClick={() => onSelectRunId(run.id)}
                >
                  <StatusIcon status={run.status} />
                  <span className={styles.runMain}>{formatRunLabel(run)}</span>
                  <span className={styles.runTime}>
                    {formatTimestamp(run.started_at, dateFormat)}
                  </span>
                  {hasFailures && (
                    <Info className={styles.infoIcon} size={14} />
                  )}
                </button>
              );
              // The failures card hovers off the whole row, so the info icon is
              // a marker within it rather than its own hover target.
              return hasFailures ? (
                <RunFailuresTooltip key={run.id} run={run}>
                  {rowButton}
                </RunFailuresTooltip>
              ) : (
                rowButton
              );
            })}
          </div>
        )}
        {runs !== null && runs.length < totalCount && (
          <Button
            className={styles.loadMore}
            block
            disabled={isLoadingList}
            onClick={() => {
              const _ = fetchPages([loadedPageCount + 1]);
            }}
          >
            Load more
          </Button>
        )}
      </div>
    </aside>
  );
}

export { formatDuration, formatMetricRuntime };
